import {
  FACE_MATCH_THRESHOLD_COSINE,
} from '@/shared/config/terminal.config';
import { db } from '@/shared/db/database';
import type { FaceEmbedding } from '../../domain/valueObjects/FaceEmbedding';
import type { FaceMatchResult, FaceMatchedEmployee } from '../../domain/entities/FaceMatchResult';
import type { IFaceMatchingRepository } from '../../domain/repositories/IFaceMatchingRepository';
import type { ImageFrame } from '../../domain/types/ImageFrame';
import type { QualityRuntimeConfig } from '../face/types';
import { FaceEmbedding as FaceEmbeddingVO } from '../../domain/valueObjects/FaceEmbedding';
import { OnnxFaceMatcher } from '../face/OnnxFaceMatcher';
import { LocalFaceEmbeddingRepository } from './LocalFaceEmbeddingRepository';
import {
  NoFaceDetectedError,
  MultipleFacesError,
  LowQualityError,
} from '../face/errors';

const AVATAR_COLORS = [
  '#2485F4', '#28C76F', '#EA5455', '#53DBD4',
  '#FF9F43', '#7367F0', '#00CFE8', '#FF6B6B',
] as const;

function colorForPerson(idPerson: number): string {
  return AVATAR_COLORS[idPerson % AVATAR_COLORS.length]!;
}

function toTitleCase(s: string): string {
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Looks up full employee display data from the local Dexie employees cache. */
async function resolveEmployeeFromCache(idPerson: number): Promise<FaceMatchedEmployee | null> {
  try {
    const row = await db.employees.get(idPerson);
    if (!row?.raw) return null;
    const raw = row.raw as Record<string, unknown>;
    const firstName = toTitleCase(String(raw['first_name'] ?? ''));
    const lastName  = toTitleCase(String(raw['last_name1'] ?? ''));
    const fullName  = `${firstName} ${lastName}`.trim();
    return {
      idPerson:       Number(raw['id_person'] ?? idPerson),
      idEntity:       Number(raw['id_entity'] ?? 0),
      code:           String(raw['code'] ?? ''),
      fullName,
      initials:       `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '??',
      avatarColor:    colorForPerson(idPerson),
      dui:            String(raw['dui_person'] ?? ''),
      department:     String(raw['entity_type'] ?? ''),
      pinBiometricId: null,
    };
  } catch {
    return null;
  }
}

export class FaceMatchingRepository implements IFaceMatchingRepository {
  private readonly matcher = OnnxFaceMatcher.getInstance();
  private readonly localEmbedRepo = new LocalFaceEmbeddingRepository();

  async initialize(): Promise<void> {
    await this.matcher.initialize();
  }

  isReady(): boolean {
    return this.matcher.isReady();
  }

  /**
   * Backward-compatible thin wrapper over `inferEmbeddingWithLiveness`.
   *
   * The scan flow now consumes `inferEmbeddingWithLiveness` directly and drives
   * the multi-frame `LivenessAggregator` in the presentation layer, so the
   * legacy single-frame 3-band classifier that lived here has been removed
   * (SDD change 2026-07-02-unify-scan-embedding-liveness — Slice B).
   *
   * Retained on the interface for external callers that only need the
   * embedding and don't care about the liveness score.
   */
  async inferEmbedding(
    frame: ImageFrame,
    runtimeConfig?: QualityRuntimeConfig,
  ): Promise<FaceEmbedding> {
    const { embedding } = await this.inferEmbeddingWithLiveness(frame, runtimeConfig);
    return embedding;
  }

  /**
   * Slice B of unify-scan-embedding-liveness: sibling method that returns the
   * embedding AND the per-frame liveness score without applying the legacy
   * 3-band classifier. Callers (e.g. `useScanLifecycle`) feed the score to a
   * `LivenessAggregator` and make the final verdict from persisted settings.
   */
  async inferEmbeddingWithLiveness(
    frame: ImageFrame,
    runtimeConfig?: QualityRuntimeConfig,
  ): Promise<{ embedding: FaceEmbedding; livenessScore: number }> {
    // Gate A: detect + quality (identical to inferEmbedding).
    const analysis = await this.matcher.analyzeQuality(
      frame as Parameters<typeof this.matcher.analyzeQuality>[0],
      undefined,
      runtimeConfig,
    );

    if (!analysis.hasOneFace || !analysis.detection) {
      const reasons = analysis.reasons.join(', ');
      if (reasons.includes('No face')) {
        throw new NoFaceDetectedError();
      }
      if (reasons.includes('faces') && !reasons.includes('No face')) {
        const match = reasons.match(/(\d+) faces/);
        const count = match ? parseInt(match[1]!, 10) : 2;
        throw new MultipleFacesError(count);
      }
      throw new NoFaceDetectedError();
    }

    // Gate B: infer embedding + raw liveness score. NO 3-band classifier here.
    const result = await this.matcher.inferEmbedding(
      frame as Parameters<typeof this.matcher.inferEmbedding>[0],
      analysis.detection,
      runtimeConfig,
    );

    if (result.kind === 'low-quality') {
      throw new LowQualityError(result.subreason, result.report);
    }

    if (result.kind !== 'embedding') {
      // Defensive: 'liveness-pending' is not emitted by inferEmbedding in current flow
      throw new NoFaceDetectedError();
    }

    return {
      embedding:     FaceEmbeddingVO.from(result.value),
      livenessScore: result.livenessScore,
    };
  }

  async match(
    probe: FaceEmbedding,
    accessRoleId: number,
    threshold = FACE_MATCH_THRESHOLD_COSINE,
  ): Promise<FaceMatchResult> {
    const records = await this.localEmbedRepo.getByAccessRole(accessRoleId);

    let bestIdPerson: number | null = null;
    let bestSim = -1;

    for (const record of records) {
      try {
        const candidate = FaceEmbeddingVO.from(record.embedding);
        const sim = probe.cosineSimilarity(candidate);
        if (sim > bestSim) {
          bestSim = sim;
          bestIdPerson = record.idPerson;
        }
      } catch {
        // invalid embedding in DB — skip
      }
    }

    if (bestSim < threshold || bestIdPerson === null) {
      return {
        employee:   null,
        confidence: Math.max(0, bestSim),
        distance:   1 - Math.max(0, bestSim),
        method:     'onnx-local',
        probe,
      };
    }

    // Resolve full employee display data from local Dexie cache
    const employee = await resolveEmployeeFromCache(bestIdPerson);

    return {
      employee,
      confidence: bestSim,
      distance:   1 - bestSim,
      method:     'onnx-local',
      probe,
    };
  }
}
